/**
 * Grenaura — Chat widget
 * File: assets/grenaura-chat.js
 *
 * Add to theme.liquid before </body>:
 *   <script src="{{ 'grenaura-chat.js' | asset_url }}" defer></script>
 *
 * Set window.GRENAURA_CHAT_ENDPOINT to your Vercel API URL.
 * In theme.liquid: <script>window.GRENAURA_CHAT_ENDPOINT = "https://your-app.vercel.app/api/chat";</script>
 */

(function () {
  "use strict";

  const ENDPOINT = window.GRENAURA_CHAT_ENDPOINT || "/api/chat";
  const MAX_HISTORY = 10; // message pairs kept in memory

  // ── State ──────────────────────────────────────────────────────────────────
  let history = [];
  let isOpen  = false;
  let isTyping = false;

  // ── Shopify context ────────────────────────────────────────────────────────
  function getShopifyContext() {
    const ctx = {};
    if (window.Shopify) {
      ctx.currentPage = document.body.getAttribute("data-page-type") || undefined;
    }
    // Product page handle
    const metaProduct = document.querySelector('meta[itemprop="name"]');
    if (metaProduct && ctx.currentPage === "product") {
      ctx.productHandle = window.location.pathname.split("/products/")[1]?.split("?")[0];
    }
    // Cart count
    const cartCount = document.querySelector(".cart-count, [data-cart-count]");
    if (cartCount) {
      ctx.cartItemCount = parseInt(cartCount.textContent) || 0;
    }
    return ctx;
  }

  // ── DOM builder ───────────────────────────────────────────────────────────
  function buildWidget() {
    const style = document.createElement("style");
    style.textContent = `
      #gr-chat-btn {
        position: fixed; bottom: 24px; right: 24px; z-index: 9999;
        width: 52px; height: 52px; border-radius: 50%; border: none; cursor: pointer;
        background: #1D9E75; color: #fff; font-size: 22px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.18); transition: transform 0.2s;
      }
      #gr-chat-btn:hover { transform: scale(1.08); }
      #gr-chat-panel {
        position: fixed; bottom: 88px; right: 24px; z-index: 9999;
        width: 340px; max-width: calc(100vw - 32px);
        border-radius: 14px; overflow: hidden;
        border: 1px solid #e0e0e0; background: #fff;
        box-shadow: 0 4px 24px rgba(0,0,0,0.12);
        display: flex; flex-direction: column;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        transition: opacity 0.2s, transform 0.2s;
      }
      #gr-chat-panel.gr-hidden { opacity: 0; pointer-events: none; transform: translateY(8px); }
      #gr-chat-header {
        background: #1D9E75; color: #fff;
        padding: 14px 16px; font-size: 14px; font-weight: 500;
        display: flex; justify-content: space-between; align-items: center;
      }
      #gr-chat-close { background: none; border: none; color: #fff; cursor: pointer; font-size: 18px; line-height: 1; }
      #gr-chat-messages {
        flex: 1; overflow-y: auto; padding: 14px;
        display: flex; flex-direction: column; gap: 10px;
        max-height: 320px; min-height: 200px;
        font-size: 13px; line-height: 1.5; color: #1a1a1a;
      }
      .gr-msg { max-width: 86%; padding: 8px 12px; border-radius: 10px; }
      .gr-msg.gr-user { align-self: flex-end; background: #E1F5EE; color: #085041; border-bottom-right-radius: 3px; }
      .gr-msg.gr-bot  { align-self: flex-start; background: #f4f4f4; color: #1a1a1a; border-bottom-left-radius: 3px; }
      .gr-msg.gr-bot a { color: #1D9E75; }
      .gr-typing { align-self: flex-start; color: #888; font-size: 12px; padding: 4px 8px; }
      #gr-chat-footer {
        display: flex; gap: 8px; padding: 10px 12px;
        border-top: 1px solid #eee; background: #fafafa;
      }
      #gr-chat-input {
        flex: 1; border: 1px solid #ddd; border-radius: 8px;
        padding: 8px 10px; font-size: 13px; outline: none;
        font-family: inherit; resize: none; max-height: 80px;
      }
      #gr-chat-input:focus { border-color: #1D9E75; }
      #gr-chat-send {
        background: #1D9E75; color: #fff; border: none; border-radius: 8px;
        padding: 8px 14px; cursor: pointer; font-size: 13px; font-weight: 500;
        transition: background 0.15s; white-space: nowrap;
      }
      #gr-chat-send:hover  { background: #0F6E56; }
      #gr-chat-send:disabled { background: #9FE1CB; cursor: not-allowed; }
    `;
    document.head.appendChild(style);

    // Button
    const btn = document.createElement("button");
    btn.id = "gr-chat-btn";
    btn.setAttribute("aria-label", "Chat with Aura, Grenaura skin advisor");
    btn.textContent = "💬";
    document.body.appendChild(btn);

    // Panel
    const panel = document.createElement("div");
    panel.id = "gr-chat-panel";
    panel.classList.add("gr-hidden");
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-label", "Grenaura skin advisor chat");
    panel.innerHTML = `
      <div id="gr-chat-header">
        <span>Aura — Grenaura Skin Advisor</span>
        <button id="gr-chat-close" aria-label="Close chat">✕</button>
      </div>
      <div id="gr-chat-messages" aria-live="polite">
        <div class="gr-msg gr-bot">Hi! I'm Aura, Grenaura's skin advisor. Ask me anything about our products, ingredients, or your skincare routine.</div>
      </div>
      <div id="gr-chat-footer">
        <textarea id="gr-chat-input" placeholder="Ask about your skin..." rows="1" aria-label="Your message"></textarea>
        <button id="gr-chat-send">Send</button>
      </div>
    `;
    document.body.appendChild(panel);

    return { btn, panel };
  }

  // ── Message rendering ──────────────────────────────────────────────────────
  function appendMessage(role, text) {
    const messages = document.getElementById("gr-chat-messages");
    const div = document.createElement("div");
    div.className = `gr-msg gr-${role === "user" ? "user" : "bot"}`;
    // Convert URLs to links in bot messages
    if (role === "assistant") {
      div.innerHTML = text
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>');
    } else {
      div.textContent = text;
    }
    messages.appendChild(div);
    messages.scrollTop = messages.scrollHeight;
    return div;
  }

  function showTyping() {
    const messages = document.getElementById("gr-chat-messages");
    const div = document.createElement("div");
    div.className = "gr-typing";
    div.id = "gr-typing-indicator";
    div.textContent = "Aura is typing…";
    messages.appendChild(div);
    messages.scrollTop = messages.scrollHeight;
  }

  function removeTyping() {
    document.getElementById("gr-typing-indicator")?.remove();
  }

  // ── Send message ───────────────────────────────────────────────────────────
  async function sendMessage() {
    const input  = document.getElementById("gr-chat-input");
    const sendBtn = document.getElementById("gr-chat-send");
    const text   = input.value.trim();
    if (!text || isTyping) return;

    input.value = "";
    input.style.height = "auto";
    isTyping = true;
    sendBtn.disabled = true;

    appendMessage("user", text);
    showTyping();

    try {
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: text,
          history: history.slice(-MAX_HISTORY),
          shopifyContext: getShopifyContext(),
        }),
      });

      removeTyping();

      if (!response.ok || !response.body) {
        appendMessage("assistant", "Sorry, I couldn't connect right now. Please try again.");
        return;
      }

      // Stream the response
      const msgDiv = appendMessage("assistant", "");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        fullText += chunk;
        // Re-render with link conversion
        msgDiv.innerHTML = fullText
          .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
          .replace(/(https?:\/\/[^\s]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>');
        document.getElementById("gr-chat-messages").scrollTop = 99999;
      }

      // Update conversation history
      history.push({ role: "user", content: text });
      history.push({ role: "assistant", content: fullText });
      if (history.length > MAX_HISTORY * 2) history = history.slice(-MAX_HISTORY * 2);

    } catch (err) {
      removeTyping();
      appendMessage("assistant", "Something went wrong. Please refresh and try again.");
      console.error("Grenaura chat error:", err);
    } finally {
      isTyping = false;
      sendBtn.disabled = false;
      input.focus();
    }
  }

  // ── Init ───────────────────────────────────────────────────────────────────
  function init() {
    const { btn, panel } = buildWidget();

    btn.addEventListener("click", () => {
      isOpen = !isOpen;
      panel.classList.toggle("gr-hidden", !isOpen);
      if (isOpen) document.getElementById("gr-chat-input").focus();
    });

    document.getElementById("gr-chat-close").addEventListener("click", () => {
      isOpen = false;
      panel.classList.add("gr-hidden");
    });

    document.getElementById("gr-chat-send").addEventListener("click", sendMessage);

    document.getElementById("gr-chat-input").addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    });

    // Auto-resize textarea
    document.getElementById("gr-chat-input").addEventListener("input", function () {
      this.style.height = "auto";
      this.style.height = Math.min(this.scrollHeight, 80) + "px";
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();